import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, MessageCircle, Calendar, Clock, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Placeholder from "@/components/Placeholder";
import { api, SITE, whatsappLink } from "@/lib/api";
import { services } from "@/lib/data";
import SEO from "@/components/SEO";

export default function Appointment() {
  const blank = { name: "", phone: "", email: "", preferred_date: "", preferred_time: "morning", service: "", message: "" };
  const [form, setForm] = useState(blank);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (success) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [success]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and phone are required");
      return;
    }
    setLoading(true);
    try {
      // Post directly to the Google Webhook to bypass the backend entirely
      await fetch(SITE.webhookUrl, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form)
      });
      
      setSuccess(true);
      toast.success("Appointment requested. We will call you shortly.");
    } catch {
      toast.error("Could not submit. Please try WhatsApp or call.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="appointment-success">
        <div className="w-16 h-16 rounded-full bg-[#FCEAE4] flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="w-8 h-8 text-[#EF734B]" strokeWidth={1.5} />
        </div>
        <h1 className="font-heading text-4xl md:text-5xl text-[#484848]">Appointment successfully requested.</h1>
        <p className="mt-4 text-[#484848]/75 max-w-md mx-auto">
          Thank you, {form.name}. Our staff will call you shortly regarding confirmation at <span className="text-[#EF734B] font-semibold">{form.phone}</span>.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <a href={`tel:${SITE.phone}`} className="btn-outline text-sm"><Phone className="w-4 h-4" /> {SITE.phoneDisplay}</a>
          <Link to="/" className="btn-primary text-sm">Back to Home <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="appointment-page">
      <SEO title="Book an Appointment" description="Schedule a consultation with Dr. Priyanka Gupta at WellMed Hospitals." />
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="overline mb-4">Book Appointment</div>
          <h1 className="font-heading text-5xl md:text-6xl text-[#484848] leading-[1.05] max-w-4xl">
            Schedule your <em className="text-[#EF734B] not-italic">private consultation.</em>
          </h1>
          <p className="mt-6 text-lg text-[#484848]/75 max-w-2xl leading-relaxed">
            Share a few details and the clinic will call you back to confirm a slot. Prefer not to fill a form? Tap WhatsApp or call directly.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
          <form onSubmit={submit} className="lg:col-span-7 bg-white rounded-3xl p-8 lg:p-10 border border-[#E8E2D9] space-y-5" data-testid="appointment-form">
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Full Name *">
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} required data-testid="appt-name" />
              </Field>
              <Field label="Phone Number *">
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} required type="tel" data-testid="appt-phone" />
              </Field>
            </div>
            <Field label="Email (optional)">
              <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} type="email" data-testid="appt-email" />
            </Field>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Preferred Date">
                <input value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} className={inputCls} type="date" data-testid="appt-date" />
              </Field>
              <Field label="Preferred Time">
                <select value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} className={inputCls} data-testid="appt-time">
                  <option value="morning">Morning (11:00 AM – 2:00 PM)</option>
                  <option value="evening">Evening (6:00 PM – 8:00 PM)</option>
                </select>
              </Field>
            </div>
            <Field label="Reason for Visit">
              <select value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className={inputCls} data-testid="appt-service">
                <option value="">Select a service…</option>
                {services.map((s) => (
                  <option key={s.slug} value={s.title}>{s.title}</option>
                ))}
                <option value="Other">Other / not sure</option>
              </select>
            </Field>
            <Field label="Brief Message (optional)">
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inputCls} h-28`} placeholder="Anything you'd like the clinic to know in advance…" data-testid="appt-message" />
            </Field>
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <button type="submit" disabled={loading} className="btn-primary disabled:opacity-60" data-testid="appt-submit">
                {loading ? "Submitting…" : "Request Appointment"} <ArrowRight className="w-4 h-4" />
              </button>
              <a href={whatsappLink()} target="_blank" rel="noreferrer" className="btn-outline text-sm" data-testid="appt-whatsapp-fallback">
                <MessageCircle className="w-4 h-4" /> Or chat on WhatsApp
              </a>
            </div>
            <div className="text-xs text-[#484848]/60 flex items-center gap-2 pt-2">
              <ShieldCheck className="w-3.5 h-3.5 text-[#EF734B]" /> Your details are private. Used only to confirm your appointment.
            </div>
          </form>

          <aside className="lg:col-span-5 space-y-6">
            <div className="bg-[#484848] text-white rounded-3xl p-8">
              <Placeholder variant="wide" label="Reception / Clinic" src="/images/facilities/DSC04279.webp" />
              <h3 className="font-heading text-2xl mt-6">Wellmed Hospitals</h3>
              <p className="text-sm text-white/70 mt-2">{SITE.address}</p>
              <div className="mt-6 space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <Clock className="w-4 h-4 mt-0.5 text-[#EF734B] shrink-0 flex-none" />
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#EF734B] font-semibold mb-1">Dr. Priyanka — consultation</div>
                    <div>Mon–Sat · 11:00 AM – 2:00 PM</div>
                    <div>Mon–Sat · 6:00 PM – 8:00 PM</div>
                    <div className="opacity-70">Sunday: Closed</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#EF734B] font-semibold mt-3 mb-1">Hospital</div>
                    <div>Open 24×7 for emergencies</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-4 h-4 mt-0.5 text-[#EF734B] shrink-0 flex-none" />
                  <a href={`tel:${SITE.phone}`} className="hover:text-[#EF734B]">{SITE.phoneDisplay}</a>
                </div>
              </div>
            </div>
            <div className="bg-[#FCEAE4]/60 rounded-3xl p-8 border border-[#EF734B]/20">
              <Calendar className="w-6 h-6 text-[#EF734B] mb-3" />
              <h3 className="font-heading text-xl text-[#484848]">What happens next?</h3>
              <ol className="mt-4 space-y-3 text-sm text-[#484848]/80">
                <li className="flex gap-3"><span className="text-[#EF734B] font-semibold">01.</span> The clinic calls to confirm a precise slot.</li>
                <li className="flex gap-3"><span className="text-[#EF734B] font-semibold">02.</span> Bring previous reports if any — or just yourself.</li>
                <li className="flex gap-3"><span className="text-[#EF734B] font-semibold">03.</span> Unhurried, private consultation with Dr. Priyanka.</li>
              </ol>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full px-4 py-3 rounded-lg border border-[#E8E2D9] bg-[#FAF7F2] focus:outline-none focus:border-[#EF734B] focus:ring-1 focus:ring-[#EF734B] text-sm";

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-[#484848]/70 font-semibold mb-1.5">{label}</label>
      {children}
    </div>
  );
}
